import { DeleteOutlineOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteProps,
  AutocompleteRenderGroupParams,
  AutocompleteValue,
  FilterOptionsState,
  IconButton,
  ListSubheader,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { kebabCase, startCase } from "lodash";
import { useConfirm } from "material-ui-confirm";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type InputHTMLAttributes,
} from "react";
import freeOptionApi from "../../api/ref/freeOptionApi";
import CodingSystems from "../../constants/CodingSystems";
import { QueryKeys } from "../../constants/queryKeys";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import { useOrganizationReference } from "../../pages/facility/hooks/useOrganization";
import { FreeOptionType } from "../../types/freeOptions";
import { createSlug } from "../../utils/createSlug";
import {
  filterRemoteFreeOptions,
  isOptionInCurrentValue,
  optionMatchesSearchQuery,
} from "../../utils/optionSearchMatch";
import { mergeCurrentValueIntoDisplayOptions } from "./TypeAheadInput/typeaheadValueUtils";
import {
  isTypeaheadBrowseMode,
  sortFreeOptionsByLabel,
  TYPEAHEAD_BROWSE_LIMITS,
} from "./TypeAheadInput/typeaheadBrowseLimits";
import {
  getTypeaheadOptionGroupKey,
  getTypeaheadOptionKey,
  isCustomTypeaheadOption,
  parseTypeaheadGroupLabel,
  ResolvedTypeaheadSectionLabels,
} from "./TypeAheadInput/typeaheadOptionSections";
import { parseFreeSoloText } from "../../utils/stringUtils";
import { ATTACHMENTS_MODALITY_SYSTEM } from "./attachmentsModalityFreeOptions";
import {
  NotificationVariant,
  useNotifications,
} from "../dashboard/NotificationsProvider";

type Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  "renderInput" | "freeSolo"
> & {
  type: FreeOptionType;
  options: T[];
  textFieldProps?: TextFieldProps;
  getExistingOption?: (
    value: string,
    options: Record<string, unknown>[],
  ) => T | undefined;
  /** When true, only org free options matching `searchQuery` are merged with API results. */
  isRemoteSearch?: boolean;
  searchQuery?: string;
  /**
   * When true, blur can commit typed text even with an empty selection.
   * Exact matches are selected; unmatched text creates an org free option.
   * Legacy call sites without this flag still match/create on blur when a
   * value is already committed or the field is multi-select.
   */
  allowCreateOnBlur?: boolean;
  /** When set, groups custom org options above reference-data results with section headers. */
  sectionLabels?: ResolvedTypeaheadSectionLabels;
};

export default function AutocompleteFreeOptions<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = true,
>({
  loading,
  onChange,
  options,
  textFieldProps,
  type,
  getExistingOption,
  isRemoteSearch = false,
  searchQuery = "",
  allowCreateOnBlur = false,
  sectionLabels,
  onInputChange,
  clearOnBlur: clearOnBlurProp,
  inputValue: controlledInputValue,
  filterOptions: incomingFilterOptions,
  ...rest
}: Props<T, Multiple, DisableClearable, FreeSolo>) {
  const organizationRef = useOrganizationReference();
  const queryClient = useQueryClient();
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const { setNotification } = useNotifications();

  // Keep local query aligned with the parent when the user clears via backspace.
  useEffect(() => {
    setActiveSearchQuery(searchQuery);
  }, [searchQuery]);

  const [, toggleLoader] = useFullPageLoader();
  const confirm = useConfirm();
  const preventBlur = useRef(false);
  const organizationId = organizationRef.identifier;

  const refreshFreeOptions = useCallback(async () => {
    const queryKey = [QueryKeys.FREE_OPTIONS, type, organizationId] as const;
    await queryClient.invalidateQueries({ queryKey });
    await queryClient.refetchQueries({ queryKey });
  }, [queryClient, type, organizationId]);

  const { data: freeOptions, isLoading: isFreeOptionsLoading } = useQuery({
    queryKey: [QueryKeys.FREE_OPTIONS, type, organizationId],
    queryFn: () =>
      freeOptionApi.getFreeOptions({
        type,
        organizationId: organizationId!,
      }),
    enabled: Boolean(organizationId),
    staleTime: 60 * 1000,
    refetchOnMount: "always",
  });

  const getOptionSearchLabel = useCallback(
    (option: T): string => {
      const getLabel = rest.getOptionLabel as ((o: T) => string) | undefined;
      if (getLabel) {
        const label = getLabel(option);
        if (label) return label;
      }
      const record = option as Record<string, unknown>;
      return (
        (record.snomedFSN as string) ||
        (record.display as string) ||
        (record.medicineName as string) ||
        (record.text as string) ||
        (record.value as string) ||
        ""
      );
    },
    [rest.getOptionLabel],
  );

  const getFreeOptionLabel = useCallback(
    (option: Record<string, unknown>) => getOptionSearchLabel(option as T),
    [getOptionSearchLabel],
  );

  const hasCommittedValue = rest.multiple
    ? Array.isArray(rest.value) && rest.value.length > 0
    : Boolean(rest.value);

  // While typing (no committed value), also honor the controlled input text so
  // free-option filtering cannot lag behind a parent searchQuery update.
  const controlledQuery =
    typeof controlledInputValue === "string" ? controlledInputValue.trim() : "";
  const effectiveSearchQuery = (
    activeSearchQuery ||
    searchQuery ||
    (!hasCommittedValue ? controlledQuery : "")
  ).trim();

  const isBrowseMode =
    isRemoteSearch && isTypeaheadBrowseMode(effectiveSearchQuery);

  const mappedFreeOptions = useMemo(
    () =>
      freeOptions?.map(({ value, _id }) => ({
        ...value,
        id: _id,
        isOrgFreeOption: true,
      })) ?? [],
    [freeOptions],
  );

  const displayOptions = useMemo(() => {
    const apiOptions = isBrowseMode
      ? (options || []).slice(0, TYPEAHEAD_BROWSE_LIMITS.SYSTEM_OPTIONS)
      : options || [];

    if (!isRemoteSearch) {
      const sortedFreeOptions = sortFreeOptionsByLabel(
        mappedFreeOptions as T[],
        getFreeOptionLabel,
      );
      return [...sortedFreeOptions, ...apiOptions];
    }

    let mergedFreeOptions = filterRemoteFreeOptions({
      freeOptions: mappedFreeOptions as T[],
      searchQuery: effectiveSearchQuery,
      value: rest.value,
      multiple: rest.multiple,
      getLabel: getFreeOptionLabel,
    });

    mergedFreeOptions = sortFreeOptionsByLabel(
      mergedFreeOptions,
      getFreeOptionLabel,
    );

    if (isBrowseMode && !hasCommittedValue) {
      mergedFreeOptions = mergedFreeOptions.slice(
        0,
        TYPEAHEAD_BROWSE_LIMITS.FREE_OPTIONS,
      );
    }

    const merged = [...mergedFreeOptions, ...apiOptions];

    return mergeCurrentValueIntoDisplayOptions({
      options: merged,
      currentValue: rest.value,
      multiple: rest.multiple,
      isOptionEqualToValue: rest.isOptionEqualToValue as
        | ((option: T, value: unknown) => boolean)
        | undefined,
    });
  }, [
    options,
    mappedFreeOptions,
    isRemoteSearch,
    isBrowseMode,
    effectiveSearchQuery,
    getFreeOptionLabel,
    hasCommittedValue,
    rest.value,
    rest.multiple,
    rest.isOptionEqualToValue,
  ]);

  const renderOptionGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => (
      <li key={params.key}>
        <ListSubheader
          component="div"
          disableSticky
          sx={{
            py: 0.75,
            px: 2,
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            lineHeight: 1.4,
            color: "text.secondary",
            textTransform: "uppercase",
            bgcolor: "background.paper",
          }}
        >
          {parseTypeaheadGroupLabel(params.group)}
        </ListSubheader>
        <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
      </li>
    ),
    [],
  );

  const handleInputChange = (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    // MUI may fire `reset` after the user backspaces to empty; ignore it so
    // browse mode and section grouping stay in sync with the cleared input.
    if (reason !== "reset") {
      setActiveSearchQuery(value ?? "");
    }
    onInputChange?.(event, value, reason);
  };

  /**
   * Final safeguard: always drop non-matching org custom options using MUI's
   * live inputValue. This catches cases where displayOptions still includes
   * browse/unfiltered free-options while the user is typing.
   */
  const filterOptions = useCallback(
    (optionsToFilter: T[], state: FilterOptionsState<T>) => {
      const afterConsumer = incomingFilterOptions
        ? incomingFilterOptions(optionsToFilter, state)
        : optionsToFilter;

      const query = state.inputValue.trim();
      if (!query) return afterConsumer;

      return afterConsumer.filter((option) => {
        if (!isCustomTypeaheadOption(option)) return true;

        const record = option as Record<string, unknown>;
        // Keep the synthetic "Add …" row for the current input.
        if (record.isCreatable) return true;

        return optionMatchesSearchQuery(record, query, (o) =>
          getOptionSearchLabel(o as T),
        );
      });
    },
    [incomingFilterOptions, getOptionSearchLabel],
  );

  const handleCreateFreeOption = async (
    value: Record<string, unknown>,
  ): Promise<boolean> => {
    try {
      toggleLoader(true);
      await freeOptionApi.createFreeOption({
        type,
        value,
        organizationId: organizationId || "",
      });
      await refreshFreeOptions();
      return true;
    } catch (error) {
      setNotification({
        message: `Error save ${startCase(type)} free option, Please try again or contact technical support.`,
        variant: NotificationVariant.error,
      });
      return false;
    } finally {
      toggleLoader(false);
    }
  };

  const prepareCreatableOption = (
    option: Record<string, unknown>,
  ): Record<string, unknown> => {
    // Clone so a failed persist does not mutate the synthetic "Add …" row.
    const prepared: Record<string, unknown> = { ...option };
    delete prepared.isCreatable;

    if (prepared.display) {
      prepared.display = parseFreeSoloText(prepared.display as string);
    } else if (prepared.snomedFSN) {
      prepared.snomedFSN = parseFreeSoloText(prepared.snomedFSN as string);
    }

    return prepared;
  };

  const handleChange = async (
    e: React.ChangeEvent,
    newValue: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
    reason?: AutocompleteChangeReason,
  ) => {
    if (Array.isArray(newValue)) {
      const lastValue = newValue[newValue.length - 1] as Record<
        string,
        unknown
      >;
      if (lastValue?.isCreatable) {
        const prepared = prepareCreatableOption(lastValue);
        const created = await handleCreateFreeOption(prepared);
        if (!created) return;

        const nextValue = [
          ...newValue.slice(0, -1),
          prepared,
        ] as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>;
        onChange?.(e, nextValue, reason);
        return;
      }
    } else if (
      typeof newValue === "object" &&
      newValue !== null &&
      (newValue as Record<string, unknown>)?.isCreatable
    ) {
      const prepared = prepareCreatableOption(
        newValue as Record<string, unknown>,
      );
      const created = await handleCreateFreeOption(prepared);
      if (!created) return;

      onChange?.(
        e,
        prepared as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
        reason,
      );
      return;
    }

    onChange?.(e, newValue, reason);
  };

  const handleDeleteFreeOption = async (id: string) => {
    try {
      if (!id) throw new Error("No id on option");

      // Only arm while delete confirm is open — never on mousedown alone,
      // or an incomplete click leaves blur/create-on-blur disabled.
      preventBlur.current = true;

      const res = await confirm({
        title: "Delete Option",
        description:
          "Are you sure you want to delete this option from the list?",
        confirmationText: "Delete",
        cancellationText: "Cancel",
        confirmationButtonProps: {
          color: "error",
        },
      })
        .then(() => true)
        .catch(() => false);

      if (!res) return;

      toggleLoader(true);

      await freeOptionApi.deleteFreeOption(id);
      await refreshFreeOptions();
    } catch (error) {
      console.error(error);
      setNotification({
        message: `Error deleting ${startCase(type)} free option, Please try again or contact technical support.`,
        variant: NotificationVariant.error,
      });
    } finally {
      toggleLoader(false);
      preventBlur.current = false;
    }
  };

  const getSystem = () => {
    switch (type) {
      case "medication-reason":
        return CodingSystems.CONDITION_CODE;
      case "family-member":
        return CodingSystems.FAMILY_MEMBER;
      case "attachments-modality":
        return ATTACHMENTS_MODALITY_SYSTEM;
      default:
        return null;
    }
  };

  const isBlurOptionAlreadySelected = (option: unknown): boolean => {
    if (option == null || rest.value == null) return false;

    const equals = rest.isOptionEqualToValue as
      | ((option: T, value: T) => boolean)
      | undefined;

    if (equals) {
      const typedOption = option as T;
      if (rest.multiple && Array.isArray(rest.value)) {
        return (rest.value as T[]).some((selected) =>
          equals(typedOption, selected),
        );
      }
      return equals(typedOption, rest.value as T);
    }

    const record = option as Record<string, unknown>;
    if (isOptionInCurrentValue(record, rest.value, rest.multiple)) {
      return true;
    }

    // performer-function style { value, display }
    if (record.value != null) {
      let selectedValues: unknown[] = [rest.value];
      if (rest.multiple) {
        selectedValues = Array.isArray(rest.value) ? rest.value : [];
      }

      return selectedValues.some(
        (selected) =>
          selected &&
          typeof selected === "object" &&
          (selected as Record<string, unknown>).value === record.value,
      );
    }

    return false;
  };

  const commitBlurOption = (option: unknown) => {
    if (isBlurOptionAlreadySelected(option)) return;

    if (rest.multiple) {
      onChange?.(
        null,
        [...((rest.value as []) || []), option] as AutocompleteValue<
          T,
          Multiple,
          DisableClearable,
          FreeSolo
        >,
        "blur",
      );
      return;
    }

    onChange?.(
      null,
      option as AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>,
      "blur",
    );
  };

  // Legacy call sites (MedicationReasonSelect, etc.) never set allowCreateOnBlur.
  // Preserve their prior behavior: blur still matches/creates when a value is
  // already committed or the field is multi-select. allowCreateOnBlur also
  // enables create/match from an empty selection (TypeAhead freeOptions).
  const canCommitOnBlur =
    allowCreateOnBlur || rest.multiple || Boolean(rest.value);

  const handleCodingBlur = async (value: string) => {
    let option: unknown;
    option = getExistingOption
      ? getExistingOption(value, displayOptions as Record<string, unknown>[])
      : displayOptions.find(
          (o) =>
            String(
              (o as Record<string, unknown>).display ?? "",
            ).toLowerCase() === value.toLowerCase(),
        );

    if (option && isBlurOptionAlreadySelected(option)) return;

    if (!option) {
      if (!canCommitOnBlur) return;

      const system = getSystem();
      option = {
        ...(system && { system }),
        code: kebabCase(`other-${value}`),
        display: value,
      };
      const created = await handleCreateFreeOption(
        option as Record<string, unknown>,
      );
      if (!created) return;
    }

    commitBlurOption(option);
  };

  const handleValueBlur = async (value: string) => {
    let option: unknown;

    option = getExistingOption
      ? getExistingOption(value, displayOptions as Record<string, unknown>[])
      : displayOptions.find(
          (o) =>
            String(
              (o as Record<string, unknown>).display ?? "",
            ).toLowerCase() === value.toLowerCase(),
        );

    if (option && isBlurOptionAlreadySelected(option)) return;

    if (!option) {
      if (!canCommitOnBlur) return;

      option = {
        value,
        display: value,
      };
      const created = await handleCreateFreeOption(
        option as Record<string, unknown>,
      );
      if (!created) return;
    }

    commitBlurOption(option);
  };

  const handleSnomedBlur = async (value: string) => {
    let option: unknown;

    option = getExistingOption
      ? getExistingOption(value, displayOptions as Record<string, unknown>[])
      : displayOptions.find(
          (o) =>
            String(
              (o as Record<string, unknown>).snomedFSN ?? "",
            ).toLowerCase() === value.toLowerCase(),
        );

    if (option && isBlurOptionAlreadySelected(option)) return;

    if (!option) {
      if (!canCommitOnBlur) return;

      option = {
        snomedCID: createSlug(value, "other"),
        snomedFSN: value,
      };
      const created = await handleCreateFreeOption(
        option as Record<string, unknown>,
      );
      if (!created) return;
    }

    commitBlurOption(option);
  };

  const handleBlur = async (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const inputText = e.target.value?.trim();

    if (preventBlur.current) return;

    if (!inputText) {
      if (rest.multiple) return;
      // Clear only when a selection was committed; while typing, value is null.
      if (rest.value) {
        onChange?.(e, null, "blur");
      }
      return;
    }

    if (!canCommitOnBlur) return;

    switch (true) {
      case type === "performer-function":
        await handleValueBlur(inputText);
        break;
      case type?.startsWith("snomed"):
        await handleSnomedBlur(inputText);
        break;
      default:
        await handleCodingBlur(inputText);
        break;
    }
  };

  return (
    <Autocomplete
      forcePopupIcon
      {...rest}
      inputValue={controlledInputValue}
      options={displayOptions}
      getOptionKey={getTypeaheadOptionKey}
      filterOptions={filterOptions}
      loading={loading || isFreeOptionsLoading}
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderInput={(params) => {
        const muiInputProps =
          params.inputProps as InputHTMLAttributes<HTMLInputElement>;
        const muiInputBlur = muiInputProps.onBlur;
        const {
          onBlur: textFieldOnBlur,
          inputProps: textFieldInputProps,
          ...restTextFieldProps
        } = textFieldProps ?? {};

        return (
          <TextField
            {...params}
            {...restTextFieldProps}
            inputProps={{
              ...muiInputProps,
              ...textFieldInputProps,
              onBlur: async (e: FocusEvent<HTMLInputElement>) => {
                // Commit create/select before MUI blur reset/close and parent sync.
                try {
                  await handleBlur(e);
                } finally {
                  muiInputBlur?.(e);
                  textFieldOnBlur?.(e);
                }
              },
            }}
          />
        );
      }}
      // Multi-select should always clear the search text on blur (chips remain).
      // Single-select: keep typed text only when blur will not match/create.
      clearOnBlur={clearOnBlurProp ?? (rest.multiple ? true : !canCommitOnBlur)}
      {...(sectionLabels
        ? {
            groupBy: (option: T) =>
              getTypeaheadOptionGroupKey(option, sectionLabels),
            renderGroup: renderOptionGroup,
          }
        : {})}
      renderOption={(props, option) => {
        const { key: _muiKey, ...optionProps } = props;
        const label = getOptionSearchLabel(option as T);
        const isOrgFreeOption = isCustomTypeaheadOption(option);
        const optionKey = getTypeaheadOptionKey(option);

        return (
          <li
            key={optionKey}
            {...optionProps}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              ...(optionProps.style as React.CSSProperties),
            }}
          >
            <span style={{ flex: 1 }}>{label}</span>
            {isOrgFreeOption && (option as Record<string, unknown>).id && (
              <IconButton
                // Keep delete out of tab order so Tab blurs the input instead of
                // focusing into the listbox (MUI then refocuses and skips clearOnBlur).
                tabIndex={-1}
                sx={{ marginLeft: "auto" }}
                color="error"
                size="small"
                onMouseDown={(e) => {
                  // Prevent input blur/focus loss before click. Do not set
                  // preventBlur here — if click never fires it would stick.
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteFreeOption(
                    (option as Record<string, unknown>).id as string,
                  );
                }}
              >
                <DeleteOutlineOutlined fontSize="small" />
              </IconButton>
            )}
          </li>
        );
      }}
    />
  );
}
